/**
 * RotationForecastCard — side-by-side preview of the user's
 * current rotation cadence against a tighter projected cadence,
 * with a one-click "Switch to N-day cadence" button that overrides
 * the default 7-day interval for this account.
 *
 * Design anchor (from the most recent PRD improvement): the same
 * mechanism Tesla uses when it shows "if you charged to 80%, your
 * battery would last 18 more months" next to the 100% option —
 * make the trade-off visible, let the user pick.
 *
 * Mechanics:
 *   - Current cadence read live from `getRotationIntervalDays()`
 *     so it reflects any earlier override.
 *   - Projected cadence is a step-tighter alternative, clamped to
 *     MIN_ROTATION_DAYS (default: 3d if current is 7d, 1d if
 *     current is 3d, etc.).
 *   - Two tiny inline SVG sparklines mirror the density of
 *     timestamps at each cadence over a 42-day window.
 *   - Button toggles between "Switch to N-day cadence" and
 *     "Revert to default" when an override is already active.
 *
 * Guard rails:
 *   - Duress-mode parent (SecurityHaloCard) never mounts this
 *     inside a decoy session; defensive checks aren't needed here.
 *   - localStorage writes go through `setRotationIntervalDays()`
 *     which clamps to [MIN, MAX].
 *   - No backend call — the override takes effect on the next
 *     login-time rotation check or manual "Rotate now" click.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Zap, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import {
  getRotationIntervalDays,
  setRotationIntervalDays,
  MIN_ROTATION_DAYS,
} from '../utils/keyRotation';

const DEFAULT_ROTATION_DAYS = 7;

// Mini sparkline: same window + visual language as the bigger
// RotationCadenceSparkline so the two feel of-a-piece.
const WINDOW_DAYS = 42;
const W = 140;
const H = 26;
const PADDING_X = 4;

const _syntheticTimestamps = (cadenceDays) => {
  const now = Date.now();
  const day = 86400_000;
  // Pack as many dots as fit inside the 42-day window, newest-
  // first, so a 3-day cadence actually looks denser than 7-day.
  const out = [];
  for (let i = 0; i * cadenceDays <= WINDOW_DAYS; i += 1) {
    out.push(now - i * cadenceDays * day);
  }
  return out.reverse(); // oldest → newest for the renderer
};

const MiniSparkline = ({ cadenceDays, accent = '#60a5fa' }) => {
  const timestamps = _syntheticTimestamps(cadenceDays);
  const now = Date.now();
  const windowMs = WINDOW_DAYS * 86400_000;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      aria-hidden
      className="block"
    >
      <line
        x1={PADDING_X} y1={H / 2}
        x2={W - PADDING_X} y2={H / 2}
        stroke="currentColor" strokeOpacity="0.18" strokeWidth="1"
      />
      {timestamps.map((ts, i) => {
        const frac = Math.max(0, 1 - (now - ts) / windowMs);
        const x = PADDING_X + frac * (W - 2 * PADDING_X);
        const isLast = i === timestamps.length - 1;
        return (
          <circle
            key={`${ts}-${i}`}
            cx={x} cy={H / 2}
            r={isLast ? 3 : 2.5}
            fill={isLast ? '#34d399' : accent}
          />
        );
      })}
    </svg>
  );
};

export const RotationForecastCard = () => {
  const [cadence, setCadence] = useState(getRotationIntervalDays());

  const hasOverride = cadence !== DEFAULT_ROTATION_DAYS;

  // Offer the single most-useful alternative — one step tighter,
  // clamped. For the typical 7-day default this is 3 days (Tesla
  // analogy: "80% charge" — noticeably safer, noticeably more
  // frequent, still sustainable).
  const projectedCadence = Math.max(MIN_ROTATION_DAYS, Math.floor(cadence / 2) || 1);
  const sameAsCurrent = projectedCadence === cadence;

  const switchTo = (days) => {
    const effective = setRotationIntervalDays(days);
    setCadence(effective);
    toast.success(
      days === DEFAULT_ROTATION_DAYS
        ? 'Reverted to default 7-day cadence'
        : `Switched to ${effective}-day cadence`
    );
  };

  return (
    <div className="space-y-2" data-testid="rotation-forecast-card">
      <label className="text-sm text-slate-400 flex items-center gap-2">
        <Zap className="w-4 h-4" />
        Cadence forecast
      </label>
      <div className="p-4 rounded-lg bg-secondary/30 space-y-4">
        <p className="text-xs text-slate-500">
          Most users never touch the default. Paranoid ones rotate
          tighter. Here's what {projectedCadence}-day cadence would
          look like next to your current {cadence}-day cadence.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <div
            className="rounded-md bg-background/40 border border-border/60 p-3"
            data-testid="forecast-current"
          >
            <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500">
              <span>Current</span>
              <span className="font-mono text-slate-300">{cadence}d</span>
            </div>
            <div className="text-sky-400 mt-2">
              <MiniSparkline cadenceDays={cadence} />
            </div>
          </div>
          <div
            className="rounded-md bg-primary/5 border border-primary/30 p-3"
            data-testid="forecast-projected"
          >
            <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-primary/80">
              <span>Projected</span>
              <span className="font-mono text-primary">{projectedCadence}d</span>
            </div>
            <div className="text-primary mt-2">
              <MiniSparkline cadenceDays={projectedCadence} accent="#60a5fa" />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!sameAsCurrent && (
            <Button
              type="button"
              size="sm"
              onClick={() => switchTo(projectedCadence)}
              className="text-xs bg-primary hover:bg-primary/90"
              data-testid="forecast-switch-btn"
            >
              <Zap className="w-3.5 h-3.5 mr-1" />
              Switch to {projectedCadence}-day cadence
            </Button>
          )}
          {hasOverride && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => switchTo(DEFAULT_ROTATION_DAYS)}
              className="text-xs"
              data-testid="forecast-revert-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" />
              Revert to default
            </Button>
          )}
          {sameAsCurrent && !hasOverride && (
            <div className="text-[11px] text-slate-500">
              Already at the tightest supported cadence.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RotationForecastCard;
